import { ElementRef, Injectable } from '@angular/core';
import { MarkdownService } from 'ngx-markdown';
import * as moment from 'moment';
@Injectable({
  providedIn: 'root',
})
export class CommonService {
  public getImageTag(path, markdownService: MarkdownService,token:string): any {
    let texting = path.substring(0, path.lastIndexOf('/'));
    markdownService.renderer.image = (
      href: string,
      title: string,
      text: string
    ) => {
      if (!texting.endsWith('/')) {
        texting += '/';
      }
      let resolvedHref =
        href.startsWith('http://') || href.startsWith('https://')
          ? href
          : new URL(href, texting).href;
      if (
        resolvedHref.includes('docs-api.cloudlabs.ai') ||
        resolvedHref.includes('docs-api-qa.cloudlabs.ai')
      ) {
        resolvedHref +=
          (resolvedHref.includes('?') ? '&' : '?') + `token=${token}`;
      }
      return `<img alt="${title || text}" src="${resolvedHref}">`;
    };
  }

  // Raw <img> tags in markdown bypass renderer.image, so append the docs-api token to their src here
  public appendDocsTokenToImages(html: string, token: string): string {
    if (!html || !token || !/<img\b/i.test(html)) {
      return html;
    }
    return html.replace(
      /(<img\b[^>]*?\bsrc\s*=\s*)(["'])([^"']*)\2/gi,
      (match, prefix, quote, src) => {
        if (
          !(src.includes('docs-api.cloudlabs.ai') ||
            src.includes('docs-api-qa.cloudlabs.ai')) ||
          /[?&]token=/.test(src)
        ) {
          return match;
        }
        const updatedSrc =
          src + (src.includes('?') ? '&' : '?') + `token=${token}`;
        return `${prefix}${quote}${updatedSrc}${quote}`;
      }
    );
  }

  // Applies appendDocsTokenToImages to every string inside an API response (e.g. inline question HTML)
  public appendDocsTokenToImagesDeep<T>(data: T, token: string): T {
    if (typeof data === 'string') {
      return this.appendDocsTokenToImages(data, token) as unknown as T;
    }
    if (Array.isArray(data)) {
      data.forEach((item, i) => {
        data[i] = this.appendDocsTokenToImagesDeep(item, token);
      });
    } else if (data && typeof data === 'object') {
      Object.keys(data).forEach((key) => {
        data[key] = this.appendDocsTokenToImagesDeep(data[key], token);
      });
    }
    return data;
  }

  getLinks(markdownService: MarkdownService) {
    markdownService.renderer.link = (
      href: string,
      title: string,
      text: string
    ) => {
      if (!href.includes('http')) {
        return (
          '<a  class="selfnavigation" tabindex="0" href="javascript:void(0)" data-id="' +
          href +
          '">' +
          text +
          '</a>'
        );
      } else {
        return (
          '<a role="link" tabindex="0" href="' +
          href +
          '" target="_blank" rel="nofollow noopener noreferrer">' +
          text +
          '</a>'
        );
      }
    };
  }

  public getAllAttributes(
    props: any,
    xmlString: string,
    attendeLabDetails: any,
    guideData: any,
    initialDeploymentData: any,
    uniqueName?: string
  ) {
    const styleTag = xmlString?.match(/style="(.*?)"/);
    const cloudProvider = xmlString.match(/cloudname\s*=\s*"(.*?)"/i);
    const enableCopy =
      xmlString?.match(/enableCopy="(.*?)"/) ||
      xmlString?.match(/enablecopy="(.*?)"/);
    const defaultValue = xmlString.match(/defaultValue\s*=\s*"(.*?)"/i);
    if (enableCopy?.length) {
      props.enableCopy = enableCopy[1] === 'true';
    }
    if (styleTag?.length) {
      props.style = styleTag[1];
    }

    const attributeRegex = /(\w+)\s*=\s*["']([^"']*)["']/g;
    let match: any;
    let combineResult = '';
    let keyLabel: any;

    while ((match = attributeRegex?.exec(xmlString)) !== null) {
      const [, attributeName, attributeValue] = match;
      if (attributeName === 'key' || attributeName === 'value') {
        let attrDynamicProperty;
        if(attributeName === 'key'){
        attrDynamicProperty = attributeValue
          ?.replace(/ +/g, '')
          ?.toLowerCase();
        } else {
          attrDynamicProperty = 'value'
        }
        const prepareResult = this.getValues(
          attrDynamicProperty,
          attributeValue,
          guideData,
          initialDeploymentData,
          attributeName === 'key',
          attendeLabDetails,
          cloudProvider ? cloudProvider[1]?.toLowerCase() : '',
          defaultValue ? defaultValue[1] : '',
          uniqueName
        );
        combineResult += prepareResult.value;
        keyLabel = prepareResult.label;
      }
    }
    return combineResult
      ? this.setGuideVariableHTML(props, combineResult, keyLabel)
      : xmlString;
  }

  private getValues(
    key: string,
    attributeValue: any,
    guideData: any,
    initialDeploymentData: any,
    isKeyAttr: boolean,
    attendeLabDetails: any,
    cloudProvider: string,
    defaultValue:string,
    uniqueName?: string
  ) {
    let deploymentOutputValues: any = {};
    let guideDataValues: any = {};
    let initialDeploymentValues: any = {};
    if (attendeLabDetails?.AllocatedTestDriveViewModalDetails.length > 1) {
      deploymentOutputValues =
        attendeLabDetails?.AllocatedTestDriveViewModalDetails.find(
          (item) => item.FriendlyName?.toLowerCase() === cloudProvider
        )?.DeploymentOutputValues;
      guideDataValues = guideData?.AllocatedTestDriveViewModalDetails.find(
        (item) => item.FriendlyName?.toLowerCase() === cloudProvider
      );
      initialDeploymentValues =
        initialDeploymentData?.AllocatedTestDriveViewModalDetails.find(
          (item) => item.FriendlyName?.toLowerCase() === cloudProvider
        );
    } else {
      deploymentOutputValues =
        attendeLabDetails?.AllocatedTestDriveViewModalDetails[0]
          ?.DeploymentOutputValues;
      guideDataValues = guideData?.AllocatedTestDriveViewModalDetails[0];
      initialDeploymentValues =
        initialDeploymentData?.AllocatedTestDriveViewModalDetails[0];
    }
    switch (key) {
      case 'value':
        return { value: attributeValue, label: 'value' };
      case 'azureaduseremail':
        return { value: guideDataValues?.AADEmail || defaultValue || '', label: 'username' };
      case 'azureaduserpassword':
        return {
          value: guideDataValues?.TempPassword || defaultValue || '',
          // label: 'password', Keeping this for future reference | Bug-7442
          label: guideDataValues?.EnableTemporaryAccessPass
            ? 'temporary access password'
            : 'password',
        };
      case 'applicationid':
      case 'accesskey':
        return { value: guideDataValues?.AADSPAppId || defaultValue || '', label: key };
      case 'displayname':
        return { value: guideDataValues?.AADSPDisplayName || defaultValue || '', label: key };
      case 'secretkey':
        return { value: guideDataValues?.AADSPAppKey || defaultValue || '', label: key };
      case 'subscriptionid':
        return { value: guideDataValues?.SubscriptionGuid || defaultValue || '', label: key };
      case 'tenantid':
        return { value: guideDataValues?.TenantId || defaultValue || '', label: key };
      case 'tenantdomainname':
        return { value: guideDataValues?.TenantDomainName || defaultValue || '', label: key };
      case 'cloudlabsdeploymentid':
        return {
          value: initialDeploymentValues?.CloudUserId || defaultValue || '',
          label: key,
        };
      case 'aaddomain':
        return { value: guideDataValues?.AADDomain || defaultValue || '', label: key };
      case 'resourcegroupregion':
        return { value: guideDataValues?.ResourceGroupRegion || defaultValue || '', label: key };
      case 'ocisigninurl':
        return { value: `https://cloud.oracle.com/?tenant=${guideDataValues?.TenantDomainName || defaultValue || ''}&domain=${guideDataValues?.AADDomain || defaultValue || ''}&amp;region=${guideDataValues?.ResourceGroupRegion || defaultValue || ''}`, label: key };
      case 'attendeeuniquename':
        return { value: initialDeploymentData?.VoucherCode || defaultValue || '', label: key };
      case 'odluniquename':
        return { value: uniqueName || defaultValue || '', label: key };
      default: {
        const deploymentObjResult: any = this.getDeploymentValues(
          deploymentOutputValues,
          key
        );
        return {
          value: deploymentObjResult || defaultValue || (isKeyAttr ? '' : attributeValue),
          label: key,
        };
      }
    }
  }

  private setGuideVariableHTML(props: any, value: any, label: any) {
    return `<span class="copydetails ${
      props.enableCopy ? '' : 'hide-copy-btn'
    }"  aria-label="${label}">
    <span ${props.style !== '' ? `style="${props.style}"` : ''}>${
      value || ''
    }</span>
    </span>`;
  }

  private getDeploymentValues(deploymentOutputValues: any, keyLabel: any) {
    let outputValue: any;
    if (deploymentOutputValues?.length > 0) {
      deploymentOutputValues.forEach((item: any) => {
        const { OutputValues } = item;
        if (
          OutputValues &&
          OutputValues.length > 0 &&
          OutputValues.findIndex(
            (value: any) =>
              value.Name.replace(/ +/g, '').toLowerCase() === keyLabel
          ) > -1
        ) {
          outputValue = OutputValues.find(
            (value: any) =>
              value.Name.replace(/ +/g, '').toLowerCase() === keyLabel
          );
        } else if (keyLabel ==='customhandleruserpassword' &&
          OutputValues &&
          OutputValues.length > 0 &&
          OutputValues.findIndex(
            (value: any) =>
              value.Name.replace(/ +/g, '').toLowerCase() === 'password'
          ) > -1
        ){
          outputValue = OutputValues.find(
            (value: any) =>
              value.Name.replace(/ +/g, '').toLowerCase() === 'password'
          );
        }
      });
    }
    return outputValue ? outputValue?.Value : '';
  }

  iso8601ToHours(isoDuration: string): number {
    const duration = moment.duration(isoDuration);
    const totalHours = duration.asHours();
    return +totalHours.toFixed(2);
  }

  iso8601ToMinutes(isoDuration: string) {
    const duration = moment.duration(isoDuration);
    const totalMinutes = duration.asMinutes();
    return +totalMinutes.toFixed(2);
  }

  minutesToISO8601(minutes) {
    if (!minutes || minutes === 0) {
      return 'PT0S';
    }

    const duration = moment.duration(minutes, 'minutes');
    let isoDuration = 'P';

    if (duration.days() > 0) {
      isoDuration += duration.days() + 'D';
    }

    isoDuration += 'T';

    if (duration.hours() > 0) {
      isoDuration += duration.hours() + 'H';
    }

    if (duration.minutes() > 0) {
      isoDuration += duration.minutes() + 'M';
    }
    return isoDuration;
  }

  hoursToISO8601(hours) {
    const duration = moment.duration(hours, 'hours');
    let isoDuration = 'P';

    const totalDays = Math.floor(hours / 24);  // Get the full days
    if (totalDays > 0) {
      isoDuration += totalDays + 'D';
    }

    if (duration.hours() > 0 || duration.minutes() > 0) {
      isoDuration += 'T';
    }

    if (duration.hours() > 0) {
      isoDuration += duration.hours() + 'H';
    }

    if (duration.minutes() > 0) {
      isoDuration += duration.minutes() + 'M';
    }
    return isoDuration;
  }

  downloadCSVFile(data: string) {
    const binaryData = [];
    binaryData.push(data);
    const mediaType = 'text/csv';
    const blob = new Blob(binaryData, { type: mediaType });
    return window.URL.createObjectURL(blob);
  }

  applyFontSize(el: ElementRef, zoomLevel: number, id: string) {
    const guidePage = el.nativeElement.querySelector(id);
    if (guidePage) {
      guidePage.style.setProperty(
        'font-size',
        `${zoomLevel * 14}px`,
        'important'
      );
      guidePage.style.setProperty('line-height', `${1.5}`, 'important');
    }
  }
}
